import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../components/Layout";
import Container from "../../../../components/Container";
import Flex from "../../../../components/Flex";
import Content from "../../../../components/Content";
import Button from "../../../../components/Button";
import ButtonList from "../../../../components/ButtonList";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import { useMessageToast } from "../../../../components/MessageProvider";
import { useFetch } from "../../../../components/useFetch";
import { useAuthState } from "../../../../components/AuthProvider";
import Label from "../../../../components/Label";
import TextBox from "../../../../components/TextBox";
import Icon from "../../../../components/Icon";
import Spinner from "../../../../components/Loader/Spinner";
import handleResponse from "../../../../utils/handleFetchResponse";
import fetchConfig from "../../../../utils/fetchConfig";

import styles from "./AddEditExternalVendor.module.scss";

const AddEditExternalVendor = () => {
  const moment = require("moment-timezone");
  const externalVendorApiUrl = `${process.env.REACT_APP_SVC_APIS_DOMAIN}/externalvendor`;

  const { id } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const { selectedClient } = user.preferences;
  const toast = useMessageToast();

  const [
    {
      data: addEditExternalVendorData,
      hasLoaded: addEditHasLoaded,
      hasError: addEditHasError
    },
    addEditFetch
  ] = useFetch();

  const [externalVendorData, setExternalVendorData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [startDateCheckValue, setStartDateCheckValue] = useState("");

  useEffect(() => {
    const config = {
      headers: {
        clientId: selectedClient.oid,
        "Content-Type": "application/json",
        userId: user.username
      }
    };
    const getExternalVendor = async () => {
      setIsLoading(true);
      const response = await fetch(
        `${externalVendorApiUrl}/get/${id}/`,
        config
      );
      if ((await response.status) == 200) {
        const data = await response.json();
        setExternalVendorData(data);
        setStartDateCheckValue(data.startDate);
        setIsLoading(false);
        setHasLoaded(true);
      } else {
        toast.error("Something went wrong while fetching the vendor");
        setIsLoading(false);
      }
    };
    if (!hasLoaded && id !== undefined) {
      getExternalVendor();
    } else {
      // this enables the add screen to show up
      setHasLoaded(true);
    }
  }, [id, hasLoaded]);

  useEffect(() => {
    if (id === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`External vendor added successfully.`);
        history.push(`/admin/externalvendors`);
      } else if (addEditHasError) {
        toast.error(`Failed to add external vendor.`);
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`External vendor updated successfully.`);
        history.push(
          `/admin/externalvendors/${addEditExternalVendorData.vendorId}`
        );
      } else if (addEditHasError) {
        toast.error(`Failed to update this external vendor.`);
      }
    }
  }, [
    id,
    addEditExternalVendorData,
    addEditHasLoaded,
    addEditHasError,
    toast,
    history
  ]);

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to="/admin/externalvendors">External Vendors</Link>
          )}
        />
        {id ? (
          <>
            <Breadcrumbs.Item
              render={() => (
                <Link to={`/admin/externalvendors/${id}`}>
                  Vendor: {externalVendorData.vendorName}
                </Link>
              )}
            />
            <Breadcrumbs.Item last render={() => "Edit"}></Breadcrumbs.Item>
          </>
        ) : (
          <Breadcrumbs.Item last render={() => `Add External Vendor`} />
        )}
      </Breadcrumbs>
    );
  }

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                {id ? (
                  <h1>Edit Vendor: {externalVendorData.vendorName}</h1>
                ) : (
                  <h1>Add Vendor</h1>
                )}
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Vendor Details</h3>
                </div>
                <div className={styles.details}>
                  <Formik
                    initialValues={{
                      vendorId: externalVendorData.vendorId,
                      vendorName: externalVendorData.vendorName,
                      vendorDesc: externalVendorData.vendorDesc
                        ? externalVendorData.vendorDesc
                        : "",
                      durId: externalVendorData.durId,
                      startDate: externalVendorData.startDate,
                      expiryDate: externalVendorData.expiryDate
                    }}
                    validationSchema={Yup.object().shape({
                      vendorName: Yup.string()
                        .trim()
                        .required("Vendor name field is required.")
                        .min(
                          1,
                          "Vendor name should be at least one character long."
                        )
                        .max(
                          100,
                          "Vendor name should be no more than 100 characters long."
                        ),
                      vendorDesc: Yup.string()
                        .trim()
                        .max(
                          500,
                          "Description should be no more than 500 characters long."
                        ),
                      durId: Yup.string()
                        .trim()
                        .required("DUR ID field is required.")
                        .matches(/^\d{6}$/, "DUR ID should be 6 numbers long."),
                      startDate: Yup.string()
                        .trim()
                        .required("Start date field is required.")
                        .test(
                          "start date validation",
                          "Start date should be a valid date formatted YYYY-MM-DD.",
                          startDate => {
                            if (!startDate) return true;
                            setStartDateCheckValue(startDate);
                            return moment(
                              startDate,
                              "YYYY-MM-DD",
                              true
                            ).isValid();
                          }
                        ),
                      expiryDate: Yup.string()
                        .trim()
                        .required("Expiry date field is required.")
                        .test(
                          "expiry date validation",
                          "Expiry date should be a valid date formatted YYYY-MM-DD.",
                          expiryDate => {
                            if (!expiryDate) return true;
                            return moment(
                              expiryDate,
                              "YYYY-MM-DD",
                              true
                            ).isValid();
                          }
                        )
                        .test(
                          "expiry date after start date validation",
                          "Expiry date should be after start date.",
                          expiryDate => {
                            if (!expiryDate || !startDateCheckValue)
                              return true;
                            console.log(
                              "startDateCheckValue: " + startDateCheckValue
                            );
                            console.log(
                              "externalVendorData.startDate: " +
                                (externalVendorData.startDate
                                  ? externalVendorData.startDate
                                  : "no vendorData")
                            );
                            return moment(
                              expiryDate,
                              "YYYY-MM-DD",
                              true
                            ).isAfter(
                              moment(startDateCheckValue, "YYYY-MM-DD", true)
                            );
                          }
                        )
                    })}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      setSubmitting(true);
                      let data = {
                        vendorName: values.vendorName,
                        vendorDesc: values.vendorDesc,
                        durId: values.durId,
                        startDate: values.startDate,
                        expiryDate: values.expiryDate
                      };
                      let method = "";
                      let url = "";

                      if (id) {
                        data["vendorId"] = id;
                        method = "put";
                        url = `${externalVendorApiUrl}/update`;
                      } else {
                        method = "post";
                        url = `${externalVendorApiUrl}/create`;
                      }

                      addEditFetch({
                        method,
                        url,
                        data
                      });

                      setSubmitting(false);
                    }}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <div>
                            {id ? (
                              <>
                                <h4>Vendor ID</h4>
                                <span>{externalVendorData.vendorId}</span>
                              </>
                            ) : null}
                          </div>
                          <div>
                            {id ? (
                              <>
                                <h4>Name</h4>
                                <span>{externalVendorData.vendorName}</span>
                              </>
                            ) : (
                              <TextBox
                                errorText={
                                  touched.vendorName ? errors.vendorName : null
                                }
                                id="vendorName"
                                label={
                                  <Label
                                    error={
                                      touched.vendorName && errors.vendorName
                                    }
                                    htmlFor="vendorName"
                                  >
                                    <b>Name</b>
                                  </Label>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                value={values.vendorName}
                                width="33%"
                              />
                            )}
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.vendorDesc ? errors.vendorDesc : null
                              }
                              id="vendorDesc"
                              label={
                                <Label
                                  error={
                                    touched.vendorDesc && errors.vendorDesc
                                  }
                                  htmlFor="vendorDesc"
                                >
                                  <b>Description</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.vendorDesc}
                              width="33%"
                            />
                          </div>
                          <div>
                            {id ? (
                              <>
                                <h4>DUR ID</h4>
                                <span>{externalVendorData.durId}</span>
                              </>
                            ) : (
                              <TextBox
                                errorText={touched.durId ? errors.durId : null}
                                id="durId"
                                label={
                                  <Label
                                    error={touched.durId && errors.durId}
                                    htmlFor="durId"
                                  >
                                    <b>DUR ID</b>
                                    <div>
                                      <span>(Six numbers)</span>
                                    </div>
                                  </Label>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                value={values.durId}
                                width="33%"
                              />
                            )}
                          </div>
                          <div>
                            {id ? (
                              <>
                                <h4>Start Date</h4>
                                <div>
                                  <span>{externalVendorData.startDate}</span>
                                </div>
                              </>
                            ) : (
                              <TextBox
                                errorText={
                                  touched.startDate ? errors.startDate : null
                                }
                                id="startDate"
                                label={
                                  <Label
                                    error={
                                      touched.startDate && errors.startDate
                                    }
                                    htmlFor="startDate"
                                  >
                                    <b>Start Date</b>
                                    <div>
                                      <span>(YYYY-MM-DD)</span>
                                    </div>
                                  </Label>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                value={values.startDate}
                                width="33%"
                              />
                            )}
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.expiryDate ? errors.expiryDate : null
                              }
                              id="expiryDate"
                              label={
                                <Label
                                  error={
                                    touched.expiryDate && errors.expiryDate
                                  }
                                  htmlFor="expiryDate"
                                >
                                  <b>Expiry Date</b>
                                  <div>
                                    <span>
                                      (YYYY-MM-DD, must be later than start
                                      date)
                                    </span>
                                  </div>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.expiryDate}
                              width="33%"
                            />
                          </div>
                          <div>
                            {id ? (
                              <>
                                <h4>Last Updated Date</h4>
                                <span>{externalVendorData.lastUpdated}</span>
                              </>
                            ) : null}
                          </div>
                          <ButtonList>
                            <Button primary type="submit">
                              {id ? (
                                "Save Changes"
                              ) : (
                                <>
                                  {"Add Vendor"}
                                  <Icon type="arrow_right" />
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => {
                                history.push(`/admin/externalvendors`);
                              }}
                            >
                              Cancel
                            </Button>
                          </ButtonList>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddEditExternalVendor;
